/**
 * Auto-maintain feedback loop (spec `specs/pending/auto-maintain.md`, Decisions
 * 11-12). When the maintenance agent can't tell whether an observed app change
 * is intentional or a bug, it calls `request_feedback` and parks the
 * convergence run (`optimization.status === 'waiting_for_feedback'`). Any org
 * member with workflow-edit permission answers the pending request here, or
 * dismisses it to cancel the parked run.
 *
 * @module FlowDesigner/hooks/useMaintenanceFeedback
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

export type MaintenanceFeedbackRequest = {
  id: string;
  workflowId: string;
  convergenceRunId: string;
  question: string;
  observedMd: string;
  ifIntentional: string;
  ifBug: string;
  status: 'pending' | 'answered' | 'canceled';
  answer: 'intentional' | 'bug' | null;
  explanation: string | null;
  answeredByUserId: string | null;
  answeredAt: string | null;
  createdAt: string;
};

const feedbackKey = (workflowId: string) => ['workflow', workflowId, 'maintenance-feedback'] as const;

/** GET /workflows/:id/maintenance-feedback — the pending request (if any) + history. */
export function useMaintenanceFeedbackQuery(workflowId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: feedbackKey(workflowId ?? ''),
    enabled: Boolean(workflowId) && enabled,
    queryFn: async () => {
      const r = await fetch(`${API_URL}/workflows/${workflowId}/maintenance-feedback`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to load maintenance feedback');
      const body = (await r.json()) as {
        ok: true;
        data: { pending: MaintenanceFeedbackRequest | null; history: MaintenanceFeedbackRequest[] };
      };
      return body.data;
    },
  });
}

/** POST /workflows/:id/maintenance-feedback/:requestId/answer — resumes the parked run. */
export function useAnswerMaintenanceFeedback(workflowId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      requestId,
      answer,
      explanation,
    }: {
      requestId: string;
      answer: 'intentional' | 'bug';
      explanation: string;
    }) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(
        `${API_URL}/workflows/${workflowId}/maintenance-feedback/${requestId}/answer`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ answer, explanation }),
        }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to answer the feedback request');
      return r.json() as Promise<{ ok: true; data: { convergenceRunId: string; jobId: string } }>;
    },
    onSuccess: () => {
      if (!workflowId) return;
      void qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      void qc.invalidateQueries({ queryKey: feedbackKey(workflowId) });
    },
  });
}

/** POST /workflows/:id/maintenance-feedback/:requestId/dismiss — cancels the parked run. */
export function useDismissMaintenanceFeedback(workflowId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      if (!workflowId) throw new Error('No workflow ID');
      const r = await fetch(
        `${API_URL}/workflows/${workflowId}/maintenance-feedback/${requestId}/dismiss`,
        { method: 'POST', credentials: 'include' }
      );
      if (!r.ok) throw await toApiError(r, 'Failed to dismiss the feedback request');
      return r.json() as Promise<{ ok: true }>;
    },
    onSuccess: () => {
      if (!workflowId) return;
      void qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      void qc.invalidateQueries({ queryKey: feedbackKey(workflowId) });
    },
  });
}
